/**
 * AVBP base component library — typed React wrappers over the locked
 * CSS primitives (styles/components.css). Screens consume THESE, never
 * raw class strings, so the semantic-colour contract is enforced in the
 * type system.
 *
 * Semantic button intent (Gandharva §9 — one fixed meaning everywhere):
 *   ink   = neutral primary "do this" (operational default)
 *   grn   = positive progression: commit / approve / advance-stage
 *   au    = ceremonial / value: report-ready, delivery, payment (INK label)
 *   ghost = neutral secondary (Reject / Edit / Source / Not-now / Cancel)
 * Terracotta is a link/urgency colour ONLY — it is intentionally NOT a
 * button intent here, by design.
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import type { L1ATabItem, L1ATabKey, StageStatus } from '../lib/l1aTabs';

type Intent = 'ink' | 'grn' | 'au' | 'ghost';

interface Btn3Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  intent: Intent;
  size?: 'md' | 'sm';
  block?: boolean;
}

const INTENT_CLASS: Record<Intent, string> = {
  ink: 'ink',
  grn: 'grn',
  au: 'au',
  ghost: 'gh',
};

export function Btn3({
  intent,
  size = 'md',
  block,
  className = '',
  children,
  ...rest
}: Btn3Props) {
  const cls = [
    'btn3',
    INTENT_CLASS[intent],
    size === 'sm' ? 'sm' : '',
    block ? 'block' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <button className={cls} {...rest}>
      {children}
    </button>
  );
}

/** Text link / urgency accent — terracotta, never a fill. */
export function Lnk({
  children,
  className = '',
  ...rest
}: React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  return (
    <a className={`avbp-lnk ${className}`} {...rest}>
      {children}
    </a>
  );
}

export function Card({
  children,
  flat,
  className = '',
  style,
  id,
}: {
  children: ReactNode;
  flat?: boolean;
  className?: string;
  style?: React.CSSProperties;
  /** Optional DOM id — used for deep-link scroll targets (e.g. Groups focus). */
  id?: string;
}) {
  return (
    <div id={id} className={`card ${flat ? 'flat' : ''} ${className}`} style={style}>
      {children}
    </div>
  );
}

/** Bounded headlined panel — every region is explicitly titled. */
export function Box({
  title,
  note,
  children,
  allowOverflow,
}: {
  title: string;
  note?: string;
  children: ReactNode;
  /**
   * `.box` clips overflow (rounded corners). Set this on a card that hosts a
   * floating layer — e.g. the ClientPicker dropdown — so the layer is not
   * sliced off at the card edge. See shell.css "overflow: hidden" note.
   */
  allowOverflow?: boolean;
}) {
  return (
    <section
      className="box"
      style={allowOverflow ? { overflow: 'visible' } : undefined}
    >
      <header className="box-h">
        <span>{title}</span>
        {note && <span className="note">{note}</span>}
      </header>
      <div className="box-body">{children}</div>
    </section>
  );
}

/**
 * ListScreen — the shared sticky-header layout for every list screen
 * (Clients · All Projects · Partners · Leads · WhatsApp Inbox).
 *
 * 2026-05-21 (Anil) — owner feedback: on a list screen the screen title,
 * the panel header and the search box all scrolled away. This component
 * pins them: the screen header (.phead) is sticky by layout; this pins the
 * panel header + the `toolbar` (search + actions); ONLY the row area
 * (`children`) scrolls, inside its own container. See STICKY LIST LAYOUT
 * in shell.css. Mobile falls back to normal page flow.
 *
 *   <ListScreen title="Clients" note="…" toolbar={<>search + buttons</>}>
 *     {rows}
 *   </ListScreen>
 *
 * `formSlot` is an optional inline create/edit form that sits between the
 * toolbar and the scroll region and stays put (it is short + contextual).
 */
export function ListScreen({
  title,
  note,
  toolbar,
  subHead,
  formSlot,
  children,
}: {
  title: string;
  note?: string;
  toolbar: ReactNode;
  /**
   * Optional pinned strip rendered BELOW the toolbar and ABOVE the scroll
   * region (e.g. a filter-tab bar). Like the toolbar it is `flex:none`, so it
   * stays fixed while only `children` (the rows) scroll. Additive — omit it and
   * the layout is unchanged.
   */
  subHead?: ReactNode;
  formSlot?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="list-screen">
      <section className="box">
        <header className="box-h">
          <span>{title}</span>
          {note && <span className="note">{note}</span>}
        </header>
        <div className="box-body">
          <div className="list-toolbar">{toolbar}</div>
          {subHead && <div className="list-subhead">{subHead}</div>}
          {formSlot && <div className="list-formslot">{formSlot}</div>}
          <div className="list-scroll">{children}</div>
        </div>
      </section>
    </div>
  );
}

/** Ambient AI chip (default, non-blocking). */
export function AiChip({
  children,
  good,
}: {
  children: ReactNode;
  good?: boolean;
}) {
  return (
    <span className={`chip ${good ? 'good' : ''}`}>
      <span className="k" aria-hidden>
        {good ? '✓' : 'AI'}
      </span>
      <span>{children}</span>
    </span>
  );
}

/** Escalated AI bubble — "needs your call" (shadow + accent left edge). */
export function AiBubble({
  eyebrow = 'Needs your call',
  children,
  actions,
}: {
  eyebrow?: string;
  children: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="aibub">
      <div className="h">
        <span className="k" aria-hidden>
          AI
        </span>
        {eyebrow}
      </div>
      <p>{children}</p>
      {actions && <div className="acts">{actions}</div>}
    </div>
  );
}

export function Tag({
  kind = 'default',
  children,
  title,
}: {
  kind?: 'default' | 'find' | 'tag' | 'val' | 'warn' | 'good';
  children: ReactNode;
  /** Optional native tooltip — e.g. the full detail behind a short chip. */
  title?: string;
}) {
  const k = kind === 'default' ? '' : kind;
  return (
    <span className={`ktag ${k}`} title={title}>
      {children}
    </span>
  );
}

export function Pill({
  on,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { on?: boolean }) {
  return (
    <button className={`pill ${on ? 'on' : ''}`} {...rest}>
      {children}
    </button>
  );
}

export interface Stage {
  label: string;
  state?: 'done' | 'now' | 'todo';
}
export function StageRail({ stages }: { stages: Stage[] }) {
  return (
    <div className="stagerail">
      {stages.map((s, i) => (
        <span key={`${i}-${s.label}`} style={{ display: 'contents' }}>
          <span className={`st ${s.state ?? ''}`}>
            <span className="dot" />
            {s.label}
          </span>
          {i < stages.length - 1 && <span className="seg" />}
        </span>
      ))}
    </div>
  );
}

/**
 * StageTabBar — the merged stage-bar-as-tabs (L1A project page). ONE horizontal
 * control fusing the read-only pipeline rail and the suppressed work-tabs:
 * Overview · the 11 L1A stages (each a tab carrying a status dot) · Settings.
 * Clicking swaps the flat stage screen below.
 *
 * Sibling of `StageRail` (which stays the read-only "Pipeline" list) — this one
 * is interactive. Visual states (the dot) are the three-way projection of the
 * rich stage state: done (filled ✓) · now (pulsing) · todo (hollow). Meta tabs
 * (Overview/Settings) render an icon instead of a dot. Matches the `.stagebar`/
 * `.nav` selectors in components.css.
 */
export function StageTabBar({
  items,
  activeKey,
  statusByKey,
  onSelect,
}: {
  items: L1ATabItem[];
  activeKey: string;
  /** Stage key → visual status. Meta tabs are ignored (no dot). */
  statusByKey: Record<string, StageStatus>;
  onSelect: (key: L1ATabKey) => void;
}) {
  const keys = items.map((i) => i.key);
  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    const idx = keys.indexOf(activeKey as L1ATabKey);
    if (idx < 0) return;
    let next = -1;
    if (e.key === 'ArrowRight') next = (idx + 1) % keys.length;
    else if (e.key === 'ArrowLeft') next = (idx - 1 + keys.length) % keys.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = keys.length - 1;
    if (next >= 0) {
      e.preventDefault();
      onSelect(keys[next]);
    }
  }

  let firstStageSeen = false;
  return (
    <div
      className="stagebar"
      role="tablist"
      aria-orientation="horizontal"
      aria-label="Project stages"
      onKeyDown={onKeyDown}
    >
      {items.map((it) => {
        const isMeta = it.meta === true;
        const status: StageStatus | undefined = isMeta
          ? undefined
          : statusByKey[it.key] ?? 'todo';
        const cls = ['nav'];
        if (isMeta) {
          cls.push('meta');
        } else {
          cls.push('stage', status as string);
          if (!firstStageSeen) {
            cls.push('first-stage');
            firstStageSeen = true;
          }
        }
        if (it.sep) cls.push('sep');
        const active = it.key === activeKey;
        if (active) cls.push('on');
        const dotChar = isMeta ? it.icon ?? '' : status === 'done' ? '✓' : '';
        const statusWord =
          status === 'done'
            ? 'done'
            : status === 'now'
              ? 'in progress'
              : status === 'todo'
                ? 'not started'
                : '';
        return (
          <button
            key={it.key}
            type="button"
            role="tab"
            id={`tab-${it.key}`}
            aria-selected={active}
            aria-controls="stage-panel"
            tabIndex={active ? 0 : -1}
            className={cls.join(' ')}
            title={it.full + (statusWord ? ` — ${statusWord}` : '')}
            onClick={() => onSelect(it.key)}
          >
            <span className="dot" aria-hidden>
              {dotChar}
            </span>
            <span className="lbl">{it.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export function EmptyNote({
  title,
  children,
}: {
  title: string;
  children?: ReactNode;
}) {
  return (
    <div className="empty-note">
      <span className="serif">{title}</span>
      {children}
    </div>
  );
}
